import React, { useMemo, useState } from "react";
import { useList } from "@/lib/hooks";
import api, { apiError } from "@/lib/api";
import { mediaUrl } from "@/lib/upload";
import { Panel } from "@/components/common";
import { UsersThree, Headset, Buildings, Truck, CaretDown, CaretRight, MagnifyingGlass, EnvelopeSimple, Phone } from "@phosphor-icons/react";
import { toast } from "sonner";

function Row({ active, onClick, icon: Icon, title, sub, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${active ? "bg-blue-600 text-white" : "hover:bg-slate-100"}`}>
      <Icon size={18} weight="duotone" className={active ? "text-white" : "text-blue-600"} />
      <span className="min-w-0 flex-1"><span className={`block text-sm font-semibold truncate ${active ? "text-white" : "text-slate-900"}`}>{title}</span>{sub && <span className={`block text-[11px] truncate ${active ? "text-white/75" : "text-slate-500"}`}>{sub}</span>}</span>
    </button>
  );
}

function Directory({ label, icon, items, openKey, setOpenKey, onPick, activeRef, testid }) {
  const [q, setQ] = useState("");
  const open = openKey === testid;
  const list = items.filter((i) => `${i.title} ${i.sub}`.toLowerCase().includes(q.toLowerCase()));
  const Icon = icon;
  return (
    <div>
      <button onClick={() => setOpenKey(open ? "" : testid)} className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg hover:bg-slate-100 text-left" data-testid={`${testid}-toggle`}>
        <Icon size={18} weight="duotone" className="text-blue-600" /><span className="flex-1 text-sm font-bold text-slate-900">{label}</span>
        <span className="font-mono text-xs text-slate-400">{items.length}</span>{open ? <CaretDown size={14} /> : <CaretRight size={14} />}
      </button>
      {open && (
        <div className="pl-3 pb-2">
          <div className="relative my-1.5"><MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${label.toLowerCase()}…`} className="w-full h-8 pl-8 pr-2 rounded-md border border-slate-200 bg-white text-xs" data-testid={`${testid}-search`} /></div>
          <div className="max-h-72 overflow-y-auto space-y-0.5">
            {list.length === 0 ? <p className="px-3 py-2 text-xs text-slate-400">Nothing found</p> : list.map((i) => <Row key={i.id} active={activeRef === i.id} onClick={() => onPick(i)} icon={Icon} title={i.title} sub={i.sub} testid={`${testid}-${i.id}`} />)}
          </div>
        </div>
      )}
    </div>
  );
}

export function MyProfileCard({ user }) {
  return (
    <div className="mt-3 rounded-xl border border-slate-200 p-3" data-testid="hub-my-profile">
      <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400 mb-2">How drivers & carriers see you</p>
      <div className="flex items-center gap-3">
        <span className="w-11 h-11 rounded-full bg-slate-100 overflow-hidden grid place-items-center font-bold text-slate-600 shrink-0">{user.avatar_url ? <img src={mediaUrl(user.avatar_url)} alt="" className="w-full h-full object-cover" /> : (user.name || "?")[0]}</span>
        <div className="min-w-0"><p className="text-sm font-bold text-slate-900 truncate">{user.name}</p><p className="text-xs text-blue-600 truncate">{user.title || "Add your position in Settings"}</p></div>
      </div>
      <p className="text-xs text-slate-600 mt-2 flex items-center gap-1.5 truncate"><EnvelopeSimple size={13} />{user.email}</p>
      <p className="text-xs text-slate-600 mt-1 flex items-center gap-1.5"><Phone size={13} />{user.direct_line || user.phone || "Add your direct line in Settings"}</p>
    </div>
  );
}

export function HubSidebar({ groups, active, setActive, user }) {
  const [openKey, setOpenKey] = useState("hub-carriers");
  const { data: carriers = [] } = useList("carriers", "/carriers");
  const { data: drivers = [] } = useList("drivers", "/drivers");
  const team = (t) => groups.find((g) => g.type === t);
  const mine = user.role === "dispatcher" ? carriers.filter((c) => c.assigned_dispatcher === user.id) : carriers;
  const carrierItems = useMemo(() => mine.map((c) => ({ id: c.id, title: c.carrier_name, sub: c.mc_number ? `MC ${c.mc_number}` : "" })), [mine]);
  const byId = useMemo(() => Object.fromEntries(carriers.map((c) => [c.id, c.carrier_name])), [carriers]);
  const driverItems = useMemo(() => drivers.map((d) => ({ id: d.id, title: (d.name || "Driver").split(" ")[0], sub: byId[d.carrier_id] || "No carrier" })), [drivers, byId]);
  const openDirect = async (kind, item) => {
    try { const { data } = await api.post("/groups/direct", { kind, ref_id: item.id }); setActive(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <Panel className="overflow-y-auto p-2" data-testid="hub-sidebar">
      {[["sales", "Sales Team", Headset, ["admin", "sales"]], ["dispatch", "Dispatch Team", UsersThree, ["admin", "dispatcher"]]].map(([t, label, Icon, roles]) => {
        const g = team(t);
        return g && roles.includes(user.role) ? <Row key={t} active={active?.id === g.id} onClick={() => setActive(g)} icon={Icon} title={label} sub="Team channel" testid={`hub-team-${t}`} /> : null;
      })}
      <div className="h-px bg-slate-200 my-2" />
      <Directory label="Carriers Group" icon={Buildings} items={carrierItems} openKey={openKey} setOpenKey={setOpenKey} onPick={(i) => openDirect("carrier", i)} activeRef={active?.ref_id} testid="hub-carriers" />
      <Directory label="Driver Group" icon={Truck} items={driverItems} openKey={openKey} setOpenKey={setOpenKey} onPick={(i) => openDirect("driver", i)} activeRef={active?.ref_id} testid="hub-drivers" />
      <MyProfileCard user={user} />
    </Panel>
  );
}
